import type { IContextMutationResult } from '@modules/core/services/query/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useToastStore } from '@modules/toast/store';

import type {
  IAdminFeesSavePayload,
  IHistoryOfAdminFeesChanges,
  ISaveAdminFeesResponse,
  IShowAdminFeesResponse,
} from '../types';
import {
  AFFILIATE_PROGRAM_ADMIN_FEES_HISTORY_LIST_QUERY_KEY,
  AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS,
  AFFILIATE_PROGRAM_SAVE_ADMIN_FEES_API_URL,
  AFFILIATE_PROGRAM_SAVE_ADMIN_FEES_MUTATION,
  AFFILIATE_PROGRAM_SHOW_ADMIN_FEES_QUERY_KEY,
} from '../constants';

export default function useSaveAdminFeesMutation(): IContextMutationResult<
  ISaveAdminFeesResponse,
  Error,
  IAdminFeesSavePayload
> {
  const { fetchPut, fetchPost } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<ISaveAdminFeesResponse, Error, IAdminFeesSavePayload>({
    mutationKey: [AFFILIATE_PROGRAM_SAVE_ADMIN_FEES_MUTATION],
    mutationFn: ({ isEdit, ...payload }) => (
      isEdit
        ? fetchPut<ISaveAdminFeesResponse>(
            AFFILIATE_PROGRAM_SAVE_ADMIN_FEES_API_URL,
            payload,
            {
              permission: AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS.EDIT,
              urlParams: {
                id: payload.history_of_admin_fees_change.id,
              },
            },
          )
        : fetchPost<ISaveAdminFeesResponse>(
            AFFILIATE_PROGRAM_SAVE_ADMIN_FEES_API_URL,
            payload,
            {
              permission: AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS.CREATE,
              urlParams: {
                id: '',
              },
            },
          )
    ),
    async onSuccess({ queryClient, variables, data }) {
      queryClient.setQueryData<IShowAdminFeesResponse>(
        [AFFILIATE_PROGRAM_SHOW_ADMIN_FEES_QUERY_KEY],
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            expected_action: data.expected_action,
            message: data.message,
            history_of_admin_fees_change: data.history_of_admin_fees_change,
          };
        },
      );

      queryClient.setQueryData<IHistoryOfAdminFeesChanges>(
        [AFFILIATE_PROGRAM_ADMIN_FEES_HISTORY_LIST_QUERY_KEY],
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            history_of_admin_fees_changes: data.history_of_admin_fees_changes,
          };
        },
      );

      displayPresetToast(variables.isEdit ? 'edited' : 'created');
    },
  });
}
